import React, { memo, useState } from 'react';
import { runOnJS, useAnimatedReaction } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { PickbarSheetSync } from '@/feature/fantasy-event-sheets/components/PickbarSheetSync';
import { FantasyPickbar, PICK_BAR_BOTTOM_DISTANCE } from '@/feature/lobby/components/FantasyPickbar';

/**
 * Pickbar for the player profile sheet
 * Appears after the sheet's mount animation completes, then translates in sync with sheet movement
 */
export const PlayerProfilePickbar = memo(() => {
    const insets = useSafeAreaInsets();
    const hasPicks = useBetslipStore(state => state.betslip.length > 0);

    const { isSheetMountCompleted, animatedIndex } = useFullScreenPagerTransition();

    // this is used to sync pickbar with the profile sheet visibility
    // we need to hide the pickbar when the sheet is closed
    const [isPlayerProfileSheetOpen, setIsPlayerProfileSheetOpen] = useState(false);

    // we need to show the pickbar only after the sheet mount animation is complete
    const [hasMountAnimationFinished, setHasMountAnimationFinished] = useState(false);

    useAnimatedReaction(
        () => isSheetMountCompleted.value,
        value => {
            runOnJS(setHasMountAnimationFinished)(value);
        }
    );

    const shouldShowPickbarOnProfileSheet = hasMountAnimationFinished && hasPicks && isPlayerProfileSheetOpen;

    return (
        <PickbarSheetSync animatedYIndex={animatedIndex} onSheetOpenChange={setIsPlayerProfileSheetOpen}>
            <FantasyPickbar
                showContent={shouldShowPickbarOnProfileSheet}
                visiblePosition={insets.bottom + PICK_BAR_BOTTOM_DISTANCE}
            />
        </PickbarSheetSync>
    );
});
